import { FormControl, FormHelperText, FormLabel, Grid } from "@mui/material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  FieldPath,
  FieldValues,
  get,
  UseControllerProps,
  useController,
} from "react-hook-form";
import { Select } from "components/Select";
import { getDaysInMonth } from "utils/getDaysInMonth";

type FormDateProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = UseControllerProps<TFieldValues, TName> & {
  label?: string;
  fullWidth?: boolean;
  required?: boolean;
};

export const FormDate = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  rules,
  defaultValue,
  shouldUnregister,
  disabled,
  fullWidth = true,
  label,
  required,
}: FormDateProps<TFieldValues, TName>) => {
  const { t } = useTranslation();

  const {
    field: { value, onChange },
    formState,
  } = useController({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
  });

  const [day, setDay] = useState<number | undefined>(undefined);
  const [month, setMonth] = useState<number | undefined>(undefined);
  const [year, setYear] = useState<number | undefined>(undefined);
  const enableDays = getDaysInMonth(month, year);
  const months = [
    t("common.january"),
    t("common.february"),
    t("common.march"),
    t("common.april"),
    t("common.may"),
    t("common.june"),
    t("common.july"),
    t("common.august"),
    t("common.september"),
    t("common.october"),
    t("common.november"),
    t("common.december"),
  ];

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDay((x) => (!x || x <= enableDays ? x : enableDays));
  }, [enableDays]);

  useEffect(() => {
    if (value) {
      const date = DateTime.fromISO(value);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDay(date.day);
      setMonth(date.month);
      setYear(date.year);
    }
  }, [value]);

  useEffect(() => {
    if (day && month && year) {
      const date = DateTime.fromObject({
        year,
        month,
        day,
      });
      if (date.isValid) {
        onChange(date.toISODate());
      }
    }
  }, [day, month, year, onChange]);

  const errorMsg = get(formState.errors, name)?.message;

  return (
    <>
      <FormLabel disabled={disabled} htmlFor={name} required={required}>
        {label}
      </FormLabel>
      <Grid container spacing={2}>
        <Grid size={4}>
          <FormLabel disabled={disabled} htmlFor={name}>
            {t("common.day")}
          </FormLabel>
          <Select
            fullWidth
            value={day ?? ""}
            onChange={(value) => setDay(value)}
            error={!!errorMsg}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <span style={{ opacity: 0.42 }}>{t("common.selectDay")}</span>
                );
              }
              return String(selected).padStart(2, "0");
            }}
            options={[...Array(enableDays)].map((_, i) => ({
              label: String(i + 1).padStart(2, "0"),
              value: i + 1,
            }))}
          />
        </Grid>
        <Grid size={4}>
          <FormLabel disabled={disabled} htmlFor={name}>
            {t("common.month")}
          </FormLabel>
          <Select
            fullWidth
            value={month ?? ""}
            onChange={(value) => setMonth(value)}
            error={!!errorMsg}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <span style={{ opacity: 0.42 }}>
                    {t("common.selectMonth")}
                  </span>
                );
              }
              return months[Number(selected) - 1];
            }}
            options={months.map((monthName, index) => ({
              label: monthName,
              value: index + 1,
            }))}
          />
        </Grid>
        <Grid size={4}>
          <FormLabel disabled={disabled} htmlFor={name}>
            {t("common.year")}
          </FormLabel>
          <Select
            fullWidth
            value={year ?? ""}
            onChange={(value) => setYear(value)}
            error={!!errorMsg}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <span style={{ opacity: 0.42 }}>
                    {t("common.selectYear")}
                  </span>
                );
              }
              return Number(selected);
            }}
            options={[...Array(100)].map((_, i) => {
              const yearValue = new Date().getFullYear() - 1 - i;
              return { label: `${yearValue}`, value: yearValue };
            })}
          />
        </Grid>
      </Grid>

      <FormControl fullWidth={fullWidth} error={!!errorMsg}>
        {errorMsg && <FormHelperText>{errorMsg}</FormHelperText>}
      </FormControl>
    </>
  );
};
